import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { I18nextProvider } from "react-i18next";
import { RouterProvider } from "react-router";

import { initMonitoring } from "@/app/monitoring";
import { devBypassAuthApi } from "@/auth/auth-api";
import { AuthProvider } from "@/auth/auth-context";
import type { AuthApi } from "@/auth/types";
import { useAuth } from "@/auth/use-auth";
import { Toaster } from "@/components/Toaster";
import { createIdentityAuthApi } from "@/features/auth/session";
import { i18n as defaultI18n } from "@/i18n";
import { type ApiConfig, ApiConfigContext, defaultApiConfig } from "@/lib/api/context";
import { env, isDev } from "@/lib/env";
import { describeError } from "@/lib/errors/describe";
import { createToastStore, ToastContext, type ToastStore } from "@/lib/toast";
import { createQueryClient } from "@/query-client";
import { SignedInPreferencesSync } from "@/features/preferences/PreferencesSync";
import { AdminRealtimeProvider } from "@/realtime/AdminRealtimeProvider";
import { createAppRouter } from "@/router";

interface AppProps {
  router?: ReturnType<typeof createAppRouter>;
  authApi?: AuthApi;
  i18n?: typeof defaultI18n;
  storage?: Storage;
  /** Gateway root + fetch (tests inject a fake backend). */
  api?: ApiConfig;
  toasts?: ToastStore;
  /** Tests look into the cache (e.g. personal data is gone after leaving a screen — T11-30). */
  queryClient?: QueryClient;
}

/** Signing out (or another staff member signing in on this tab) drops every cached answer. */
function ClearCacheOnSignOut({ onSignOut }: { onSignOut: () => void }) {
  const { state } = useAuth();
  const previous = useRef(state.status);
  useEffect(() => {
    if (
      state.status === "anonymous" &&
      previous.current !== "loading" &&
      previous.current !== "anonymous"
    ) {
      onSignOut();
    }
    previous.current = state.status;
  }, [state.status, onSignOut]);
  return null;
}

export function App({
  router,
  authApi,
  i18n = defaultI18n,
  storage,
  api = defaultApiConfig,
  toasts,
  queryClient: injectedQueryClient,
}: AppProps) {
  const [toastStore] = useState(() => toasts ?? createToastStore());
  const [queryClient] = useState(
    () =>
      injectedQueryClient ??
      createQueryClient({
        toasts: toastStore,
        describe: (error) => describeError(error, i18n.t.bind(i18n)),
      }),
  );
  const [appRouter] = useState(() => router ?? createAppRouter());
  const [authBoundary] = useState<AuthApi>(
    () =>
      authApi ??
      (isDev && env.VITE_AUTH_DEV_BYPASS
        ? devBypassAuthApi
        : createIdentityAuthApi({
            baseUrl: api.baseUrl,
            ...(api.fetch ? { fetch: api.fetch } : {}),
            getLocale: () => i18n.resolvedLanguage,
          })),
  );

  useEffect(() => {
    void initMonitoring();
  }, []);
  const clearAll = useCallback(() => {
    queryClient.clear();
    toastStore.clear();
  }, [queryClient, toastStore]);

  return (
    <I18nextProvider i18n={i18n}>
      <ApiConfigContext value={api}>
        <ToastContext value={toastStore}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider api={authBoundary} storage={storage}>
              <ClearCacheOnSignOut onSignOut={clearAll} />
              {/* T11-52: one realtime connection per tab — every screen updates itself (R21) */}
              <AdminRealtimeProvider>
                {/* T01-17: the signed-in person's preferences follow them to every computer */}
                <SignedInPreferencesSync />
                <RouterProvider router={appRouter} />
              </AdminRealtimeProvider>
              <Toaster store={toastStore} />
            </AuthProvider>
          </QueryClientProvider>
        </ToastContext>
      </ApiConfigContext>
    </I18nextProvider>
  );
}
